import {
  createFinancialAidRequirementPolicySchema,
  FinancialAidRequirementPolicySchema,
  FinancialAidRequirementStateSchema,
  listUnknownFinancialAidRequirementKeys,
  type FinancialAidRequirementPolicy,
  type FinancialAidRequirementField,
  type FinancialAidRequirementState,
} from '@triedge/staff-settings-domain';

export type YstnjFinancialAidRequirementCondition =
  | { kind: 'always' }
  | { kind: 'parentIsActive'; parentKey: 'father' | 'mother' }
  | { kind: 'housingType'; value: 'own' | 'rent' }
  | { kind: 'arrayRow'; arrayKey: string }
  | { kind: 'booleanPath'; path: string; value: boolean };

export type YstnjFinancialAidRequirementField =
  FinancialAidRequirementField<YstnjFinancialAidRequirementCondition>;

export const FINANCIAL_AID_REQUIREMENT_FIELDS: readonly YstnjFinancialAidRequirementField[] = [
  {
    fieldKey: 'father.occupation',
    path: 'father.occupation',
    step: 0,
    label: "Father's occupation",
    group: 'Parents',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'father.employer',
    path: 'father.employer',
    step: 0,
    label: "Father's employer",
    group: 'Parents',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'mother.occupation',
    path: 'mother.occupation',
    step: 0,
    label: "Mother's occupation",
    group: 'Parents',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'mother.employer',
    path: 'mother.employer',
    step: 0,
    label: "Mother's employer",
    group: 'Parents',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeFather.gross2024',
    path: 'incomeFather.gross2024',
    step: 1,
    label: 'Father gross income',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Father gross income is required. Enter 0 if there was no income.',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.parsonage',
    path: 'incomeFather.parsonage',
    step: 1,
    label: 'Father parsonage',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Father parsonage is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.fedTax',
    path: 'incomeFather.fedTax',
    step: 1,
    label: 'Father federal tax withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.stateTax',
    path: 'incomeFather.stateTax',
    step: 1,
    label: 'Father state tax withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.ficaTax',
    path: 'incomeFather.ficaTax',
    step: 1,
    label: 'Father FICA withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.refunds',
    path: 'incomeFather.refunds',
    step: 1,
    label: 'Father tax refunds / credits',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Father tax refunds / credits is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeFather.estimated2025',
    path: 'incomeFather.estimated2025',
    step: 1,
    label: 'Father estimated income (upcoming year)',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Father estimated income is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'father' },
  },
  {
    fieldKey: 'incomeMother.gross2024',
    path: 'incomeMother.gross2024',
    step: 1,
    label: 'Mother gross income',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Mother gross income is required. Enter 0 if there was no income.',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.parsonage',
    path: 'incomeMother.parsonage',
    step: 1,
    label: 'Mother parsonage',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Mother parsonage is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.fedTax',
    path: 'incomeMother.fedTax',
    step: 1,
    label: 'Mother federal tax withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.stateTax',
    path: 'incomeMother.stateTax',
    step: 1,
    label: 'Mother state tax withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.ficaTax',
    path: 'incomeMother.ficaTax',
    step: 1,
    label: 'Mother FICA withheld',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.refunds',
    path: 'incomeMother.refunds',
    step: 1,
    label: 'Mother tax refunds / credits',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Mother tax refunds / credits is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'incomeMother.estimated2025',
    path: 'incomeMother.estimated2025',
    step: 1,
    label: 'Mother estimated income (upcoming year)',
    group: 'Income',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Mother estimated income is required. Enter 0 if none.',
    condition: { kind: 'parentIsActive', parentKey: 'mother' },
  },
  {
    fieldKey: 'benefits.healthInsurance',
    path: 'benefits.healthInsurance',
    step: 1,
    label: 'Employer paid health insurance',
    group: 'Company benefits & gifts',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Employer paid health insurance is required. Enter 0 if not applicable.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'benefits.tuition',
    path: 'benefits.tuition',
    step: 1,
    label: 'Employer paid tuition',
    group: 'Company benefits & gifts',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Employer paid tuition is required. Enter 0 if not applicable.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'benefits.auto',
    path: 'benefits.auto',
    step: 1,
    label: 'Auto allowance',
    group: 'Company benefits & gifts',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Auto allowance is required. Enter 0 if not applicable.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'benefits.other',
    path: 'benefits.other',
    step: 1,
    label: 'Other company benefits',
    group: 'Company benefits & gifts',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Other company benefits is required. Enter 0 if not applicable.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'assets.mortgageYears',
    path: 'assets.mortgageYears',
    step: 2,
    label: 'Total years of mortgage',
    group: 'Assets',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'housingType', value: 'own' },
  },
  {
    fieldKey: 'expenses.studentLoans',
    path: 'expenses.studentLoans',
    step: 3,
    label: 'Student loans',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.carInsurance',
    path: 'expenses.carInsurance',
    step: 3,
    label: 'Car insurance',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.healthInsurance',
    path: 'expenses.healthInsurance',
    step: 3,
    label: 'Health insurance',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.lifeInsurance',
    path: 'expenses.lifeInsurance',
    step: 3,
    label: 'Life insurance',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.shulDues',
    path: 'expenses.shulDues',
    step: 3,
    label: 'Shul dues',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.cleaningHelp',
    path: 'expenses.cleaningHelp',
    step: 3,
    label: 'Cleaning help',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.other',
    path: 'expenses.other',
    step: 3,
    label: 'Other monthly expenses',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'expenses.medicalCoverage',
    path: 'expenses.medicalCoverage',
    step: 3,
    label: 'Medical coverage',
    group: 'Monthly expenses',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.homeImprovements',
    path: 'majorExp.homeImprovements',
    step: 5,
    label: 'Home improvement amount',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Home improvement amount is required. Enter 0 if none.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.homeImprovementsDetails',
    path: 'majorExp.homeImprovementsDetails',
    step: 5,
    label: 'Home improvement description',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.largePurchases',
    path: 'majorExp.largePurchases',
    step: 5,
    label: 'Large purchase amount',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Large purchase amount is required. Enter 0 if none.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.largePurchasesDetails',
    path: 'majorExp.largePurchasesDetails',
    step: 5,
    label: 'Large purchase description',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.simchaType',
    path: 'majorExp.simchaType',
    step: 5,
    label: 'Planned simcha description',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.simchaCost',
    path: 'majorExp.simchaCost',
    step: 5,
    label: 'Planned simcha cost',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Planned simcha cost is required. Enter 0 if none.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.loanAmount',
    path: 'majorExp.loanAmount',
    step: 5,
    label: 'Scholarship amount accepted as interest-free loan',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Interest-free loan amount is required. Enter 0 if none.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'majorExp.extraordinaryObligations',
    path: 'majorExp.extraordinaryObligations',
    step: 5,
    label: 'Extra obligations or circumstances',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'vacation.cost',
    path: 'vacation.cost',
    step: 5,
    label: 'Vacation total cost',
    group: 'Lifestyle & expenditures',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Vacation total cost is required. Enter 0 if none.',
    condition: { kind: 'booleanPath', path: 'vacation.traveled', value: true },
  },
  {
    fieldKey: 'camps2025.0.childName',
    path: 'camps2025.0.childName',
    step: 5,
    label: 'Current-year camp child name (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Enter a child name for the current-year camp row. Write "None" if no children attended camp.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2025' },
  },
  {
    fieldKey: 'camps2025.0.campName',
    path: 'camps2025.0.campName',
    step: 5,
    label: 'Current-year camp name (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Enter a camp name for the current-year camp row. Write "None" if no children attended camp.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2025' },
  },
  {
    fieldKey: 'camps2025.0.fee',
    path: 'camps2025.0.fee',
    step: 5,
    label: 'Current-year camp fee (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Current-year camp fee is required. Enter 0 if none.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2025' },
  },
  {
    fieldKey: 'camps2024.0.childName',
    path: 'camps2024.0.childName',
    step: 5,
    label: 'Prior-year camp child name (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Enter a child name for the prior-year camp row. Write "None" if no children attended camp.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2024' },
  },
  {
    fieldKey: 'camps2024.0.campName',
    path: 'camps2024.0.campName',
    step: 5,
    label: 'Prior-year camp name (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Enter a camp name for the prior-year camp row. Write "None" if no children attended camp.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2024' },
  },
  {
    fieldKey: 'camps2024.0.fee',
    path: 'camps2024.0.fee',
    step: 5,
    label: 'Prior-year camp fee (first row)',
    group: 'Summer camps',
    togglable: true,
    defaultRequirement: 'optional',
    message: 'Prior-year camp fee is required. Enter 0 if none.',
    condition: { kind: 'arrayRow', arrayKey: 'camps2024' },
  },
  {
    fieldKey: 'request.offeredTuition',
    path: 'request.offeredTuition',
    step: 6,
    label: 'Family offer: tuition payment',
    group: 'Tuition request',
    togglable: true,
    defaultRequirement: 'required',
    message: 'Family tuition offer is required.',
    condition: { kind: 'always' },
  },
  {
    fieldKey: 'request.offeredFees',
    path: 'request.offeredFees',
    step: 6,
    label: 'Family offer: fees payment',
    group: 'Tuition request',
    togglable: true,
    defaultRequirement: 'optional',
    condition: { kind: 'always' },
  },
] as const;

export const YSTNJ_FINANCIAL_AID_REQUIREMENT_POLICY_SCHEMA =
  createFinancialAidRequirementPolicySchema(FINANCIAL_AID_REQUIREMENT_FIELDS);

export const parseYstnjFinancialAidRequirementPolicy = (
  policy: unknown,
): FinancialAidRequirementPolicy =>
  YSTNJ_FINANCIAL_AID_REQUIREMENT_POLICY_SCHEMA.parse(policy);

export const listUnknownYstnjFinancialAidRequirementKeys = (
  policy: FinancialAidRequirementPolicy | undefined,
): string[] => listUnknownFinancialAidRequirementKeys(
  policy,
  FINANCIAL_AID_REQUIREMENT_FIELDS,
);

export const sanitizeYstnjFinancialAidRequirementPolicy = (
  policy: unknown,
): FinancialAidRequirementPolicy => {
  const parsed = FinancialAidRequirementPolicySchema.parse(policy);
  const fieldRequirements = Object.fromEntries(
    Object.entries(parsed.fieldRequirements).filter(([fieldKey, requirementState]) => {
      if (listUnknownYstnjFinancialAidRequirementKeys({ fieldRequirements: { [fieldKey]: requirementState } }).length) {
        return false;
      }
      return FinancialAidRequirementStateSchema.safeParse(requirementState).success;
    }),
  );
  return parseYstnjFinancialAidRequirementPolicy({ fieldRequirements });
};

export const isYstnjFinancialAidRequirementFieldVisible = (
  policy: FinancialAidRequirementPolicy | undefined,
  field: YstnjFinancialAidRequirementField,
): boolean => (
  (policy?.fieldRequirements?.[field.fieldKey] ?? field.defaultRequirement) !== 'hidden'
);

export const toYstnjFinancialAidRequirementState = (
  value: unknown,
  fallback: FinancialAidRequirementState = 'optional',
): FinancialAidRequirementState => (
  FinancialAidRequirementStateSchema.safeParse(value).success
    ? value as FinancialAidRequirementState
    : fallback
);

export const assertKnownYstnjFinancialAidRequirementPolicy: (
  policy: unknown,
) => asserts policy is FinancialAidRequirementPolicy = (
  policy: unknown,
) => {
  parseYstnjFinancialAidRequirementPolicy(policy);
};
